// src/polaris-components/TeamMemberCard.tsx
import React, { useState } from "react";
import Link from "./Link";
import s from "../styles/ProfileHeader.module.css";
import "../styles/team-members.css";
import ProfileAvatar from "./ProfileAvatar";

export type TeamMember = {
    userId: number;
    name: string;
    email?: string;
    imageUrl?: string;           // Pre-signed S3 URL from backend
    profileImageUrl?: string;    // Alternative field name for image URL
    logo?: boolean;
    href?: string;
    roleLabel?: string;
    roleName?: string;
};

// Default fallback avatar - a simple gray circle SVG
const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzIiIGhlaWdodD0iNzIiIHZpZXdCb3g9IjAgMCA3MiA3MiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8Y2lyY2xlIGN4PSIzNiIgY3k9IjM2IiByPSIzNiIgZmlsbD0iI2U1ZTdlYiIvPgogIDxwYXRoIGQ9Ik0zNiAzOGM0LjQxOCAwIDgtMy41ODIgOC04cy0zLjU4Mi04LTgtOC04IDMuNTgyLTggOCAzLjU4MiA4IDggOHptMCA0Yy01LjMzMyAwLTE2IDIuNjY3LTE2IDh2NGgzMnYtNGMwLTUuMzMzLTEwLjY2Ny04LTE2LTh6IiBmaWxsPSIjYThhOWFkIi8+Cjwvc3ZnPg==';

export default function TeamMemberCard({
                                           userId,
                                           name,
                                           email,
                                           imageUrl,
                                           profileImageUrl,
                                           logo = false,
                                           href,
                                           roleLabel,
                                           roleName,
                                       }: TeamMember) {
    // Use pre-signed S3 URL directly, or fallback to default avatar
    const actualImageUrl = imageUrl ?? profileImageUrl;
    const [imageError, setImageError] = useState(false);

    // Determine src: use S3 URL if available and no error, otherwise fallback
    const imgSrc = (actualImageUrl && !imageError) ? actualImageUrl : DEFAULT_AVATAR;

    const handleImageError = () => {
        setImageError(true);
    };

    const to = href ?? (userId != null ? `/team/view/${userId}` : "/team/view");
    const roleText = (roleName ?? roleLabel ?? "").trim();
    const roleKey = roleText.toLowerCase();

    // Map backend role to UI-friendly display text
    let displayText = "";
    if (roleKey.includes("admin")) {
        displayText = "Admin";
    } else if (roleKey === "staff") {
        displayText = "Staff";
    } else if (roleKey === "coach") {
        displayText = "Coach";
    }

    const variantClass =
        roleKey === "administrator" || roleKey === "admin"
            ? s.badgeAdmin
            : roleKey === "staff"
                ? s.badgeStaff
                : roleKey === "coach"
                    ? s.badgeCoach
                    : s.badgeNeutral;

    const badgeClass = `${s.badge} ${s.badgePin} ${variantClass}`;

    return (
        <article className={`tm-card ${s.card}`}>
            {displayText && <span className={badgeClass}>{displayText}</span>}

            <div className="tm-body">
                {logo ? (
                    <img
                        className="tm-logo"
                        src={imgSrc}
                        alt={`${name} logo`}
                        loading="lazy"
                        decoding="async"
                        onError={handleImageError}
                    />
                ) : (
                    <ProfileAvatar
                        displayName={name}
                        imageUrl={actualImageUrl}
                        className="tm-avatar"
                        alt={`${name} photo`}
                        allowUpload={false}
                        skipAvatarFetch={true}
                    />
                )}
                <h3 className="tm-name">{name}</h3>
                {email && (
                    <a className="tm-email" href={`mailto:${email}`}>
                        {email}
                    </a>
                )}
            </div>

            <Link to={to} userId={userId.toString()} className="tm-bar">
                View Team
            </Link>
        </article>
    );
}
